import { Action, createReducer, on } from '@ngrx/store';
import { BadResponseWithError } from '@shared/models/common.interface';

import { RewardsActions } from '../actions/actions-type.action';

export interface RewardsState {
    challenges: any[];
    evergreens: any[];
    isLoading: boolean;
    error: BadResponseWithError | null;
    categoriesForGroupRewards: any;
    eventTypes: any;
    perks?: any[];
}

const initialState: RewardsState = {
    challenges: [],
    evergreens: [],
    isLoading: false,
    error: null,
    categoriesForGroupRewards: null,
    eventTypes: [],
    perks: [],
};

const reducer = createReducer(
    initialState,
    on(RewardsActions.fetchCategoriesForRewardsRequests, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RewardsActions.fetchCategoriesForRewardsSuccess, (state, action) => ({
        ...state,
        categoriesForGroupRewards: action.payload,
        isLoading: false,
    })),
    on(RewardsActions.fetchEventTypesForRewardsSuccess, (state, action) => ({
        ...state,
        eventTypes: action.payload,
        isLoading: false,
    })),
    on(RewardsActions.fetchCategoriesForRewardsFailure, (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
    })),
    on(RewardsActions.fetchChallengesRequests, (state) => ({
        ...state,
        challenges: [],
        isLoading: true,
    })),
    on(RewardsActions.fetchChallengesSuccess, (state, action) => {
        const transformed = transformObjectToStateObject(action.payload).sort(
            (a: any, b: any) => a.displayOrderNumber - b.displayOrderNumber
        );

        return {
            ...state,
            challenges: [...transformed],
            isLoading: false,
        };
    }),
    on(RewardsActions.fetchChallengesFailure, (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
    })),

    on(RewardsActions.fetchPerksRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RewardsActions.fetchPerksSuccess, (state, action) => ({
        ...state,
        perks: action.payload.perks,
        isLoading: false,
    })),
    on(RewardsActions.fetchPerksFailure, (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
    })),

    on(RewardsActions.createSingleRewardsSuccess, (state, action) => {
        const r = action.payload.payload || action.payload;
        const transformed = {
            id: r.id,
            type: 'challenge',
            categoryId: r.categoryId,
            name: r.name,
            description: r.description,
            isActive: r.isActive,
            rewardsDetails: r.rewardsDetails,
            startsAt: r.startsAt,
            expiresAt: r.expiresAt,
            createdAt: r.createdAt,
            eventType: r.eventType,
            nextProgressResetAt: r.nextProgressResetAt,
            progressResetPeriod: r.progressResetPeriod,
            progressResetAnchorTime: r.progressResetAnchorTime,
            completionsCountNeededForReward: r.completionsCountNeededForReward,
            autoResetOnCompletion: r.autoResetOnCompletion,
            targetId: r.targetId,
            displayOrderNumber:
                typeof r.displayOrderNumber === 'number'
                    ? r.displayOrderNumber
                    : state.challenges.reduce(
                          (m: number, it: any) =>
                              Math.max(m, it.displayOrderNumber || 0),
                          0
                      ) + 1,
            isGroup: false,
        } as any;
        if (
            state.challenges.every(
                (c) => c.categoryId === transformed.categoryId
            )
        ) {
            const merged = [...state.challenges, transformed].sort(
                (a: any, b: any) => a.displayOrderNumber - b.displayOrderNumber
            );
            return {
                ...state,
                challenges: merged,
                isLoading: false,
            };
        }
        return {
            ...state,
            isLoading: false,
        };
    }),
    on(RewardsActions.createGroupRewardsSuccess, (state, action) => {
        const r = action.payload.payload || action.payload;
        const info = r;
        const transformed = {
            id: info.id,
            type: 'group',
            categoryId: info.categoryId,
            name: info.name,
            description: info.description,
            isActive: info.isActive,
            rewardsDetails: info.rewardsDetails,
            startsAt: info.startsAt,
            expiresAt: info.expiresAt,
            createdAt: info.createdAt,
            eventType: info.eventType,
            nextProgressResetAt: info.nextProgressResetAt,
            progressResetPeriod: info.progressResetPeriod,
            progressResetAnchorTime: info.progressResetAnchorTime,
            targetId: info.targetId,
            displayOrderNumber:
                typeof info.displayOrderNumber === 'number'
                    ? info.displayOrderNumber
                    : state.challenges.reduce(
                          (m: number, it: any) =>
                              Math.max(m, it.displayOrderNumber || 0),
                          0
                      ) + 1,
            challengesInfo: Array.isArray(info.challengesInfo)
                ? info.challengesInfo
                : [],
            isGroup: true,
        } as any;
        if (
            state.challenges.every(
                (c) => c.categoryId === transformed.categoryId
            )
        ) {
            const merged = [...state.challenges, transformed].sort(
                (a: any, b: any) => a.displayOrderNumber - b.displayOrderNumber
            );
            return {
                ...state,
                challenges: merged,
                isLoading: false,
            };
        }
        return {
            ...state,
            isLoading: false,
        };
    }),
    on(RewardsActions.updateSingleRewardsSuccess, (state, action) => {
        const updated = state.challenges.map((challenge: any) => {
            return challenge.id === action.payload.challengeId
                ? { ...challenge, ...action.payload.body }
                : challenge;
        });
        return {
            ...state,
            challenges: updated.sort(
                (a: any, b: any) => a.displayOrderNumber - b.displayOrderNumber
            ),
            isLoading: false,
        };
    }),
    on(RewardsActions.updateGroupRewardsSuccess, (state, action) => {
        const updated = state.challenges.map((challenge: any) => {
            return challenge.id === action.payload.groupId
                ? { ...challenge, ...action.payload.body }
                : challenge;
        });
        return {
            ...state,
            challenges: updated.sort(
                (a: any, b: any) => a.displayOrderNumber - b.displayOrderNumber
            ),
            isLoading: false,
        };
    }),
    on(RewardsActions.deleteChallengeSuccess, (state, action) => {
        return {
            ...state,
            challenges: state.challenges
                .filter(
                    (challenge: any) =>
                        challenge.isGroup ||
                        challenge.id !== action.payload.challengeId
                )
                .map((item: any) => {
                    if (item.isGroup && Array.isArray(item.challengesInfo)) {
                        return {
                            ...item,
                            challengesInfo: item.challengesInfo.filter(
                                (ch: any) =>
                                    ch.id !== action.payload.challengeId
                            ),
                        };
                    }
                    return item;
                }),
            isLoading: false,
        };
    }),
    on(RewardsActions.deleteGroupChallengeSuccess, (state, action) => {
        return {
            ...state,
            challenges: state.challenges.filter((challenge: any) => {
                return challenge.id !== action.payload.groupId;
            }),
            isLoading: false,
        };
    }),
    on(
        RewardsActions.createChallengeForGroupRewardsSuccess,
        (state, { payload }) => {
            const { groupId, challenge } = payload;
            return {
                ...state,
                challenges: state.challenges.map((item: any) => {
                    if (item.isGroup && item.id === groupId) {
                        const list = Array.isArray(item.challengesInfo)
                            ? item.challengesInfo
                            : [];
                        return {
                            ...item,
                            challengesInfo: [...list, challenge],
                        };
                    }
                    return item;
                }),
            };
        }
    ),
    on(
        RewardsActions.updateChallengeForGroupRewardsSuccess,
        (state, { payload }) => {
            const { groupId, challengeId, body } = payload;
            return {
                ...state,
                challenges: state.challenges.map((item: any) => {
                    if (item.isGroup && item.id === groupId) {
                        const updatedList = (item.challengesInfo || []).map(
                            (ch: any) =>
                                ch.id === challengeId ? { ...ch, ...body } : ch
                        );
                        return { ...item, challengesInfo: updatedList };
                    }
                    return item;
                }),
            };
        }
    ),
    on(RewardsActions.changeDisplayOrderRequests, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RewardsActions.changeDisplayOrderSuccess, (state, { payload }) => {
        const mapOrder = new Map(
            payload.displayNumbers.map((d) => [
                d.entityId,
                d.displayOrderNumber,
            ])
        );
        const updated = state.challenges.map((item: any) =>
            mapOrder.has(item.id)
                ? { ...item, displayOrderNumber: mapOrder.get(item.id) }
                : item
        );
        return {
            ...state,
            challenges: updated.sort(
                (a: any, b: any) => a.displayOrderNumber - b.displayOrderNumber
            ),
            isLoading: false,
        };
    }),
    on(RewardsActions.changeDisplayOrderFailure, (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
    })),
    on(RewardsActions.fetchEvergreensRequest, (state) => ({
        ...state,
        evergreens: [],
        isLoading: true,
    })),
    on(RewardsActions.fetchEvergreensSuccess, (state, action) => ({
        ...state,
        evergreens: [...action.payload],
        isLoading: false,
    })),
    on(RewardsActions.fetchEvergreensFailure, (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
    })),
    on(RewardsActions.createEvergreensSuccess, (state, action) => {
        const r = action.payload.payload || action.payload;
        const transformed = {
            id: r.id,
            name: r.name,
            displayText: r.displayText,
            isActive: r.isActive,
            rewardsDetails: r.rewardsDetails,
            eventType: r.eventType,
        } as any;

        return {
            ...state,
            evergreens: [...state.evergreens, transformed],
            isLoading: false,
        };
    }),
    on(RewardsActions.updateEvergreensSuccess, (state, action) => {
        const updated = state.evergreens.map((challenge: any) => {
            return challenge.id === action.payload.challengeId
                ? { ...challenge, ...action.payload.body }
                : challenge;
        });
        return {
            ...state,
            evergreens: updated,
            isLoading: false,
        };
    })
);

function transformObjectToStateObject(object: any) {
    return object.flatMap((item: any) => {
        if (!item) return [];
        if (item.isGroup && item.group) {
            const info = item.group.groupInfo;
            return [
                {
                    id: info.id,
                    type: 'group',
                    categoryId: info.categoryId,
                    name: info.name,
                    description: info.description,
                    isActive: info.isActive,
                    /* lidomaPointRewardAmount: info.lidomaPointRewardAmount,
                    xpRewardAmount: info.xpRewardAmount,*/
                    rewardsDetails: info.rewardsDetails,
                    startsAt: info.startsAt,
                    expiresAt: info.expiresAt,
                    createdAt: info.createdAt,
                    eventType: info.eventType,
                    nextProgressResetAt: info.nextProgressResetAt,
                    progressResetPeriod: info.progressResetPeriod,
                    progressResetAnchorTime: info.progressResetAnchorTime,
                    displayOrderNumber: item.displayOrderNumber,
                    challengesInfo: item.group.challengesInfo,
                    isGroup: true,
                },
            ];
        } else if (!item.isGroup && item.challenge) {
            const info = item.challenge.challengeInfo;
            return [
                {
                    id: info.id,
                    type: 'challenge',
                    categoryId: info.categoryId,
                    name: info.name,
                    description: info.description,
                    isActive: info.isActive,
                    rewardsDetails: info.rewardsDetails,
                    startsAt: info.startsAt,
                    expiresAt: info.expiresAt,
                    createdAt: info.createdAt,
                    eventType: info.eventType,
                    targetId: info.sourceId,
                    nextProgressResetAt: info.nextProgressResetAt,
                    progressResetPeriod: info.progressResetPeriod,
                    progressResetAnchorTime: info.progressResetAnchorTime,
                    displayOrderNumber: item.displayOrderNumber,
                    completionsCountNeededForReward:
                        info.completionsCountNeededForReward,
                    autoResetOnCompletion: info.autoResetOnCompletion,
                    isGroup: false,
                },
            ];
        }
        return [];
    });
}

export const RewardsReducer = (state: RewardsState, action: Action) =>
    reducer(state, action);
